import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { get, post } from 'stores/_services/dataService';
import { clearWalkMe } from '../common/walkMe/walkMe';

const TOKEN_KEY = 'auth-token';
const TTL_KEY = 'auth-ttl';
const RULE_ACCESS_KEY = 'accessRule';
const APP_VERSION_NUMBER_KEY = 'applicationVersionNumber';
const SSO_LOGOUT_URL = 'ssoLogoutUrl';

export interface IAuthSettings {
    audience: string;
    auth0ClientId: string;
    auth0Url: string;
    authenticationMethod: string;
    logoutUrl: string;
    ssoEnabled: boolean;
    dattoSsoLogoutUrl: string;
    auth0CallbackUrl?: string;
    dattoAuthorizationUrl: string;
    k1AuthorizationUrl: string | null;
}

export default class AuthStore {
    ttl = sessionStorage.getItem(TTL_KEY);
    token = sessionStorage.getItem(TOKEN_KEY);
    ssoLogoutUrl = sessionStorage.getItem(SSO_LOGOUT_URL);
    settings?: IAuthSettings;

    get authenticated(): boolean {
        return !!this.token;
    }

    constructor() {
        makeObservable(this, {
            ttl: observable,
            token: observable,
            settings: observable,
            setAuthToken: action,
            setAuthTTL: action,
            removeAuthInfo: action,
            removeRuleInfo: action,
            authenticated: computed,
            login: action,
            logout: action,
            refreshAccessToken: action,
            getAuthenticationSettings: action
        });
    }

    setAuthToken(token: string): void {
        this.token = token;
        sessionStorage.setItem(TOKEN_KEY, token);
    }

    setAuthTTL(ttl: string): void {
        this.ttl = dayjs().add(parseInt(ttl), 'seconds').toString();

        if (this.ttl) {
            sessionStorage.setItem(TTL_KEY, this.ttl);
        }
    }

    setSsoLogoutUrl(ssoLogoutUrl: string | null): void {
        this.ssoLogoutUrl = ssoLogoutUrl;
        sessionStorage.setItem(SSO_LOGOUT_URL, ssoLogoutUrl || '');
    }

    setAccessRule(ruleAccess: boolean): void {
        sessionStorage.setItem(RULE_ACCESS_KEY, JSON.stringify(ruleAccess));
    }

    getAccessRule(): boolean | null {
        return JSON.parse(sessionStorage.getItem(RULE_ACCESS_KEY));
    }

    setApplicationVersionNumber(versionNumber: string): void {
        sessionStorage.setItem(APP_VERSION_NUMBER_KEY, versionNumber);
    }

    getApplicationVersionNumber(): string | null {
        return sessionStorage.getItem(APP_VERSION_NUMBER_KEY);
    }

    removeAuthInfo(): void {
        this.token = null;
        this.ttl = null;
        sessionStorage.removeItem(TTL_KEY);
        sessionStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(SSO_LOGOUT_URL);
        this.removeWalkMeInfo();
        this.removeApplicationVersionNumber();
        this.removeRuleInfo();
    }

    removeRuleInfo(): void {
        sessionStorage.removeItem(RULE_ACCESS_KEY);
    }

    removeWalkMeInfo(): void {
        clearWalkMe();
    }

    removeApplicationVersionNumber(): void {
        sessionStorage.removeItem(APP_VERSION_NUMBER_KEY);
    }

    async login(username: string, password: string, token?: string): Promise<void> {
        const res = await fetch('/api/users/login', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                username,
                password,
                token
            })
        });

        if (!res.ok) {
            const data = await res.json();
            if (data.error.message === 'k1LoginRequired') {
                res.message = 'This user is required to use KaseyaOne login';
            } else {
                res.message = data.error.message;
            }
            throw res;
        }

        const data = await res.json();

        this.setAuthToken(data.id);
        this.setAuthTTL(data.ttl);
        this.setSsoLogoutUrl(null);
    }

    async logout(): Promise<void> {
        if (!this.ssoLogoutUrl && this.authenticated) {
            try {
                await post('users/me/logout');
            } catch (err) {
                console.error(`Error logging out: ${err}`);
            }
        }
        const loginType = Cookies.get('loginType');

        this.removeAuthInfo();
        Cookies.set('loginType', 'logout', { expires: 1, domain: 'infocyte.com' });
        if (this.ssoLogoutUrl && loginType == 'kaseya1') {
            window.location.href = this.ssoLogoutUrl;
            return;
        } else if (this.ssoLogoutUrl && loginType == 'authweb') {
            try {
                await fetch(this.ssoLogoutUrl, {
                    method: 'GET',
                    credentials: 'include'
                });
            } catch (error) {
                console.error(`Error  logging out: ${error}`);
            }
        }

        window.location.href = '/login';
    }

    async refreshAccessToken(): Promise<void> {
        let res;

        try {
            res = await get('users/me/refreshAccessToken');
        } catch (e) {
            //TODO: Error logging
            return;
        }

        const { data } = res;

        this.setAuthToken(data.id);
        this.setAuthTTL(data.ttl);
    }

    async getAuthenticationSettings(): Promise<IAuthSettings | null> {
        if (this.settings) {
            return this.settings;
        }

        const urlParams = new URLSearchParams(window.location.search);

        const res = await get('authentication/settings', {
            queryParams: {
                login_hint: urlParams.get('login_hint')
            }
        });

        const { data } = res;
        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.settings = {
                ...this.settings,
                ...data
            };
        });

        return this.settings;
    }
}
